/* eslint-disable no-template-curly-in-string */
import React, { useContext, useEffect, useReducer, useState } from "react"
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom"
import { Collapse, List } from "@material-ui/core"
import { ChevronDown, ChevronUp, CalendarCheck, ClipboardList } from "lucide-react"
import ListItem from "@material-ui/core/ListItem"
import ListItemIcon from "@material-ui/core/ListItemIcon"
import ListItemText from "@material-ui/core/ListItemText"
import ListSubheader from "@material-ui/core/ListSubheader"
import { Badge } from "@material-ui/core"

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext"
import { AuthContext } from "../context/Auth/AuthContext"
import { Can } from "../components/Can"
import { SocketContext } from "../context/Socket/SocketContext"
import { isArray } from "lodash"

import { makeStyles } from "@material-ui/core/styles"

import usePlans from "../hooks/usePlans"
import useVersion from "../hooks/useVersion"
import { useQueryClient } from "react-query"
import { prefetchKanbanFluxoForcar } from "../hooks/useKanbanQueries/prefetchKanbanFluxoInicial"
import { prefetchDashboardCompleto } from "../hooks/useDashboardQueries/prefetchDashboardPeriodo"
import { prefetchConversasCompleto } from "../hooks/useConversasQueries/prefetchConversasInicial"
import { prefetchRespostasRapidasPagina } from "../hooks/useRespostasRapidasQueries/prefetchRespostasRapidasPagina"
import { prefetchUsuariosPagina } from "../hooks/useUsuariosQueries/prefetchUsuariosPagina"
import { prefetchSituacoesPagina } from "../hooks/useSituacoesQueries/prefetchSituacoesPagina"
import { usuarioCampanhasEmailLiberado } from "../helpers/usuarioCampanhasEmailLiberado"

import {
  Calendar,
  BookUser,
  SquareKanban,
  Tag,
  Zap,
  ScrollText,
  Plug,
  Bot,
  UsersRound,
  Bolt,
  LayoutDashboard,
  Globe,
} from "lucide-react"

import WhatsAppIcon from "../components/WhatsappIcon"
import useCompanyBackofficeIntegrada from "../hooks/useCompanyBackofficeIntegrada"

const useStyles = makeStyles((theme) => ({
  ListSubheader: {
    height: 26,
    marginTop: "-15px",
    marginBottom: "-10px",
  },
  iconArrow: {
    color: theme.palette.neutral400,
  },
  ListBox: {
    display: "flex",
    height: "56px",
    marginLeft: "8px",
    marginRight: "8px",
    //padding: "var(--Padding-padding-200, 16px)", padding antigo
    justifyContent: "space-between",
    alignItems: "center",
    alignSelf: "stretch",
    borderRadius: "12px",
  },
  ListItem: {
    display: "flex",
    alignItems: "center",
    gap: "var(--Spacing-spacing-400, 16px)",
    flex: "1 0 0",
    borderRadius: "12px",
  },
  ListIcon: {
    color: theme.palette.neutral400,
  },
  ListText: {
    fontFamily: "var(--font-family-body, Roboto)",
    fontSize: "var(--font-size-16, 16px)",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "125%" /* 20px */,
    letterDpacing: "-0.04px",
    color: theme.palette.neutral400,
    borderRadius: "12px",
    transition: "opacity 0.25s ease",
  },
  divider: {
    backgroundColor: theme.palette.borderColorBar,
    width: "100%",
  },
}))

function ListItemLink(props) {
  const { icon, primary, to, className, textColor, iconColor, locationPathname, drawerOpen, onPrefetch } = props

  const classes = useStyles()

  const renderLink = React.useMemo(
    () => React.forwardRef((itemProps, ref) => <RouterLink to={to} ref={ref} {...itemProps} />),
    [to],
  )

  return (
    <li>
      <ListItem
        button
        component={renderLink}
        className={className && classes.ListBox}
        style={{ width: props.drawerOpen ? "304px" : "56px" }}
        onMouseEnter={onPrefetch}
        onTouchStart={onPrefetch}
        onFocus={onPrefetch}
      >
        <ListItemIcon style={{ color: locationPathname === to ? iconColor : "" }} className={classes.ListIcon}>
          {icon}
        </ListItemIcon>
        <ListItemText
          className={classes.ListText}
          style={{
            color: locationPathname === to ? textColor : "",
            marginLeft: "-15px",
            opacity: drawerOpen ? 1 : 0,
          }}
          primary={primary}
        ></ListItemText>
      </ListItem>
    </li>
  )
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload
    const newChats = []

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id)
        if (chatIndex !== -1) {
          state[chatIndex] = chat
        } else {
          newChats.push(chat)
        }
      })
    }

    return [...state, ...newChats]
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload
    const chatIndex = state.findIndex((u) => u.id === chat.id)

    if (chatIndex !== -1) {
      state[chatIndex] = chat
      return [...state]
    } else {
      return [chat, ...state]
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload

    const chatIndex = state.findIndex((u) => u.id === chatId)
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1)
    }
    return [...state]
  }

  if (action.type === "RESET") {
    return []
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat
      }
      return chat
    })
    return changedChats
  }
}

const MainListItems = (props) => {
  const location = useLocation()
  const classes = useStyles()

  const { drawerClose, collapsed } = props
  const { whatsApps } = useContext(WhatsAppsContext)
  // eslint-disable-next-line no-unused-vars
  const { user, handleLogout } = useContext(AuthContext)
  const { lIntegrada: lBackofficeIntegrado } = useCompanyBackofficeIntegrada()
  const [connectionWarning, setConnectionWarning] = useState(false)
  const [openCampaignSubmenu, setOpenCampaignSubmenu] = useState(false)
  const [showCampaigns, setShowCampaigns] = useState(false)
  const [showKanban, setShowKanban] = useState(false)
  const [showOpenAi, setShowOpenAi] = useState(false)
  const [showIntegrations, setShowIntegrations] = useState(false)
  const history = useHistory()
  // eslint-disable-next-line no-unused-vars
  const [showSchedules, setShowSchedules] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [showInternalChat, setShowInternalChat] = useState(false)
  const [showExternalApi, setShowExternalApi] = useState(false)

  const [invisible, setInvisible] = useState(true)
  const [pageNumber, setPageNumber] = useState(1)
  const [searchParam] = useState("")
  const [chats, dispatch] = useReducer(reducer, [])
  const { getPlanCompany } = usePlans()
  const queryClient = useQueryClient()

  const lidarPrefetchKanbanFluxo = () =>
    prefetchKanbanFluxoForcar(queryClient, { lIncluirFluxo: false })
  const lidarPrefetchRespostasRapidas = () => prefetchRespostasRapidasPagina(user)
  const lidarPrefetchUsuarios = () => prefetchUsuariosPagina(user)

  // eslint-disable-next-line no-unused-vars
  const [version, setVersion] = useState(false)

  const { getVersion } = useVersion()

  const socketManager = useContext(SocketContext)

  useEffect(() => {
    async function fetchVersion() {
      const _version = await getVersion()
      setVersion(_version.version)
    }
    fetchVersion()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch({ type: "RESET" })
    setPageNumber(1)
  }, [searchParam])

  useEffect(() => {
    let lCancelado = false

    const carregarPlano = async () => {
      const companyId = user.companyId
      const planConfigs = await getPlanCompany(undefined, companyId)
      if (lCancelado) return

      setShowCampaigns(planConfigs.plan.useCampaigns)
      setShowKanban(planConfigs.plan.useKanban)
      setShowOpenAi(planConfigs.plan.useOpenAi)
      setShowIntegrations(planConfigs.plan.useIntegrations)
      setShowSchedules(planConfigs.plan.useSchedules)
      setShowInternalChat(planConfigs.plan.useInternalChat)
      setShowExternalApi(planConfigs.plan.useExternalApi)
    }

    const nAgendamento =
      typeof requestIdleCallback !== "undefined"
        ? requestIdleCallback(() => carregarPlano())
        : setTimeout(carregarPlano, 0)

    return () => {
      lCancelado = true
      if (typeof requestIdleCallback !== "undefined" && typeof cancelIdleCallback !== "undefined") {
        cancelIdleCallback(nAgendamento)
      } else {
        clearTimeout(nAgendamento)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber])
  */

  useEffect(() => {
    const companyId = localStorage.getItem("companyId")
    const socket = socketManager.getSocket(companyId)

    socket.on(`company-${companyId}-chat`, (data) => {
      if (data.action === "new-message") {
        dispatch({ type: "CHANGE_CHAT", payload: data })
      }
      if (data.action === "update") {
        dispatch({ type: "CHANGE_CHAT", payload: data })
      }
    })
    return () => {
      socket.disconnect()
    }
  }, [socketManager])

  /*
  useEffect(() => {
    let unreadsCount = 0
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false)
    } else {
      setInvisible(true)
    }
  }, [chats, user.id])
  */

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          )
        })
        if (offlineWhats.length > 0) {
          setConnectionWarning(true)
        } else {
          setConnectionWarning(false)
        }
      }
    }, 2000)
    return () => clearTimeout(delayDebounceFn)
  }, [whatsApps])

  /*
  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      })
      dispatch({ type: "LOAD_CHATS", payload: data.records })
    } catch (err) {
      toastError(err)
    }
  }
  */

  /*
  const handleClickLogout = () => {
    //handleCloseMenu();
    handleLogout();
  };*/

  // Função para verificar se a rota atual é /conversas ou começa com /conversas/
  const isConversasActive = location.pathname === "/conversas" || location.pathname.startsWith("/conversas/")

  // Regra: Campanhas para Certtus (companyId=2), Clickey (companyId=26), admin ou emails liberados
  const nCompanyId = Number(localStorage.getItem("companyId"))
  const lEmpresaCerttus = nCompanyId === 2 || nCompanyId === 26
  const lUsuarioAdmin = user?.profile === "admin" || usuarioCampanhasEmailLiberado(user?.email)
  const lPodeMostrarCampanhas = lEmpresaCerttus && lUsuarioAdmin

  return (
    <div onClick={drawerClose}>
      <ListItemLink
        className={classes.ListBox}
        to="/conversas"
        primary="Conversas"
        icon={<WhatsAppIcon />}
        textColor="#009FFD"
        iconColor="#009FFD"
        locationPathname={isConversasActive ? "/conversas" : location.pathname}
        drawerOpen={props.drawerOpen}
        onPrefetch={prefetchConversasCompleto}
      />
      {showKanban && (
        <>
          <ListItemLink
            className={classes.ListBox}
            to="/kanban"
            primary="Kanban de conversas"
            icon={<SquareKanban strokeWidth={1.6} size={"24px"} />}
            textColor="#009FFD"
            iconColor="#009FFD"
            locationPathname={location.pathname}
            drawerOpen={props.drawerOpen}
            onPrefetch={lidarPrefetchKanbanFluxo}
          />
          {/* Fluxo de conversas desabilitado
          {user.profile !== "user" && (
            <ListItemLink
              className={classes.ListBox}
              to="/fluxo"
              primary="Fluxo de conversas"
              icon={<ArrowLeftRight strokeWidth={1.6} size={"24px"} />}
              textColor="#009FFD"
              iconColor="#009FFD"
              locationPathname={location.pathname}
              drawerOpen={props.drawerOpen}
              onPrefetch={lidarPrefetchKanbanFluxo}
            />
          )}
          */}
        </>
      )}
      {showSchedules && (
        <ListItemLink
          className={classes.ListBox}
          to="/schedules"
          primary="Mensagens agendadas"
          icon={<Calendar strokeWidth={1.6} size={"24px"} />}
          textColor="#009FFD"
          iconColor="#009FFD"
          locationPathname={location.pathname}
          drawerOpen={props.drawerOpen}
        />
      )}

      <ListItemLink
        className={classes.ListBox}
        to="/contacts"
        primary="Contatos"
        icon={<BookUser strokeWidth={1.6} size={"24px"} />}
        textColor="#009FFD"
        iconColor="#009FFD"
        locationPathname={location.pathname}
        drawerOpen={props.drawerOpen}
      />
      {/*
      <ListItemLink
        className={classes.ListBox}
        to="/chats"
        primary="Chat Interno"
        icon={
          <Badge color="secondary" variant="dot" invisible={invisible}>
            <MessageCircle strokeWidth={1.6} size={"24px"} />
          </Badge>
        }
        textColor="#009FFD"
        iconColor="#009FFD"
        locationPathname={location.pathname}
        drawerOpen={props.drawerOpen}
      />
      */}

      <Can
        role={user.profile}
        perform="dashboard:view"
        yes={() => (
          <ListItemLink
            className={classes.ListBox}
            to="/dashboard"
            primary="Dashboard"
            icon={<LayoutDashboard strokeWidth={1.6} size={"24px"} />}
            textColor="#009FFD"
            iconColor="#009FFD"
            locationPathname={location.pathname}
            drawerOpen={props.drawerOpen}
            onPrefetch={prefetchDashboardCompleto}
          />
        )}
      />
      <ListItemLink
        className={classes.ListBox}
        to="/situacoes"
        primary="Situações"
        icon={<Tag strokeWidth={1.6} size={"24px"} />}
        textColor="#009FFD"
        iconColor="#009FFD"
        locationPathname={location.pathname}
        drawerOpen={props.drawerOpen}
        onPrefetch={prefetchSituacoesPagina}
      />
      <ListItemLink
        className={classes.ListBox}
        to="/quick-messages"
        primary="Respostas rápidas"
        icon={<Zap strokeWidth={1.6} size={"24px"} />}
        textColor="#009FFD"
        iconColor="#009FFD"
        locationPathname={location.pathname}
        drawerOpen={props.drawerOpen}
        onPrefetch={lidarPrefetchRespostasRapidas}
      />

      <ListSubheader
        size={"24px"}
        hidden={collapsed}
        style={{
          position: "relative",
          fontSize: "17px",
          textAlign: "left",
          paddingLeft: 20,
        }}
        inset
        color="inherit"
      ></ListSubheader>

      {showCampaigns && lPodeMostrarCampanhas && (
        <>
          <ListItem className={classes.ListBox} button onClick={() => setOpenCampaignSubmenu((prev) => !prev)}>
            <div className={classes.ListItem}>
              <ListItemIcon className={classes.ListIcon}>
                <CalendarCheck strokeWidth={1.6} size={"24px"} />
              </ListItemIcon>
              <ListItemText
                primary={props.drawerOpen ? "Campanhas" : "Campanhas"}
                style={{
                  opacity: props.drawerOpen ? 1 : 0,
                  marginLeft: "-32px",
                  marginRight: "32px",
                }}
                className={classes.ListText}
              />
              {openCampaignSubmenu ? (
                <ChevronUp className={classes.iconArrow} />
              ) : (
                <ChevronDown className={classes.iconArrow} />
              )}
            </div>
          </ListItem>
          <Collapse style={{ paddingLeft: 16 }} in={openCampaignSubmenu} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                className={classes.ListBox}
                style={{
                  color: location.pathname === "/campaigns" ? "#009FFD" : "",
                }}
                onClick={() => history.push("/campaigns")}
                button
              >
                <div className={classes.ListItem}>
                  <ListItemIcon className={classes.ListIcon}>
                    <ClipboardList
                      size={"24px"}
                      strokeWidth={1.6}
                      style={{
                        color: location.pathname === "/campaigns" ? "#009FFD" : "",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Campanhas"
                    className={classes.ListText}
                    style={{
                      marginLeft: "-30px",
                      color: location.pathname === "/campaigns" ? "#009FFD" : "",
                      opacity: props.drawerOpen ? 1 : 0,
                    }}
                  />
                </div>
              </ListItem>
              <ListItem
                className={classes.ListBox}
                style={{
                  color: location.pathname === "/contact-lists" ? "#009FFD" : "",
                }}
                onClick={() => history.push("/contact-lists")}
                button
              >
                <div className={classes.ListItem}>
                  <ListItemIcon className={classes.ListIcon}>
                    <UsersRound
                      size={"24px"}
                      strokeWidth={1.6}
                      style={{
                        color: location.pathname === "/contact-lists" ? "#009FFD" : "",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Listas de contatos"
                    className={classes.ListText}
                    style={{
                      marginLeft: "-30px",
                      color: location.pathname === "/contact-lists" ? "#009FFD" : "",
                      opacity: props.drawerOpen ? 1 : 0,
                    }}
                  />
                </div>
              </ListItem>
              <ListItem
                className={classes.ListBox}
                style={{
                  color: location.pathname === "/campaigns-config" ? "#009FFD" : "",
                }}
                onClick={() => history.push("/campaigns-config")}
                button
              >
                <div className={classes.ListItem}>
                  <ListItemIcon className={classes.ListIcon}>
                    <Bolt
                      size={"24px"}
                      strokeWidth={1.6}
                      style={{
                        color: location.pathname === "/campaigns-config" ? "#009FFD" : "",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Configurações"
                    className={classes.ListText}
                    style={{
                      marginLeft: "-30px",
                      color: location.pathname === "/campaigns-config" ? "#009FFD" : "",
                      opacity: props.drawerOpen ? 1 : 0,
                    }}
                  />
                </div>
              </ListItem>
            </List>
          </Collapse>
        </>
      )}
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            {user.super && (
              <ListItemLink
                className={classes.ListBox}
                to="/announcements"
                primary="Informativos"
                icon={<ScrollText strokeWidth={1.6} size={"24px"} />}
                textColor="#009FFD"
                iconColor="#009FFD"
                locationPathname={location.pathname}
                drawerOpen={props.drawerOpen}
              />
            )}
            {/*
            {showOpenAi && (
              <ListItemLink
                className={classes.ListBox}
                to="/prompts"
                primary="Open.Ai"
                icon={<Infinity strokeWidth={1.6} size={"24px"} />}
                textColor="#009FFD"
                iconColor="#009FFD"
                locationPathname={location.pathname}
                drawerOpen={props.drawerOpen}
              />
            )}
            */}

            {/*
            {showIntegrations && (
              <ListItemLink
                className={classes.ListBox}
                to="/queue-integration"
                primary="Integrações"
                icon={<Workflow strokeWidth={1.6} size={"24px"} />}
                textColor="#009FFD"
                iconColor="#009FFD"
                locationPathname={location.pathname}
                drawerOpen={props.drawerOpen}
              />
            )}
            */}

            <ListItemLink
              to="/connections"
              className={classes.ListBox}
              primary="Conexões"
              icon={
                <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
                  <Plug strokeWidth={1.6} size={"24px"} />
                </Badge>
              }
              textColor="#009FFD"
              iconColor="#009FFD"
              locationPathname={location.pathname}
              drawerOpen={props.drawerOpen}
            />

            {/*
            <ListItemLink
              className={classes.ListBox}
              to="/files"
              primary="Lista de arquivos"
              icon={<Paperclip strokeWidth={1.6} size={"24px"} />}
              textColor="#009FFD"
              iconColor="#009FFD"
              locationPathname={location.pathname}
              drawerOpen={props.drawerOpen}
            />
            */}

            <ListItemLink
              className={classes.ListBox}
              to="/queues"
              primary="Filas"
              icon={<Bot strokeWidth={1.6} size={"24px"} />}
              textColor="#009FFD"
              iconColor="#009FFD"
              locationPathname={location.pathname}
              drawerOpen={props.drawerOpen}
            />
            <ListItemLink
              className={classes.ListBox}
              to="/users"
              primary="Usuários"
              icon={<UsersRound strokeWidth={1.6} size={"24px"} />}
              textColor="#009FFD"
              iconColor="#009FFD"
              locationPathname={location.pathname}
              drawerOpen={props.drawerOpen}
              onPrefetch={lidarPrefetchUsuarios}
            />
            {lBackofficeIntegrado === true && (
              <ListItemLink
                className={classes.ListBox}
                to="/portal-cliente"
                primary="Portal do Cliente"
                icon={<Globe strokeWidth={1.6} size={"24px"} />}
                textColor="#009FFD"
                iconColor="#009FFD"
                locationPathname={location.pathname}
                drawerOpen={props.drawerOpen}
              />
            )}
            {/*
            {showExternalApi && (
              <>
                <ListItemLink
                  className={classes.ListBox}
                  to="/messages-api"
                  primary="API"
                  icon={<Code strokeWidth={1.6} size={"24px"} />}
                  textColor="#009FFD"
                  iconColor="#009FFD"
                  locationPathname={location.pathname}
                  drawerOpen={props.drawerOpen}
                />
              </>
            )}
            */}
            <ListItemLink
              className={classes.ListBox}
              to="/settings"
              primary="Configurações"
              icon={<Bolt strokeWidth={1.6} size={"24px"} />}
              textColor="#009FFD"
              iconColor="#009FFD"
              locationPathname={location.pathname}
              drawerOpen={props.drawerOpen}
            />
          </>
        )}
      />
    </div>
  )
}

export default MainListItems
